/**
 * App Component
 *
 * 라우팅 및 레이아웃 설정
 * - admin/user 구분 없이 AppShell + Sidebar 사용
 * - 메뉴/권한은 DDB(agentBuilder_menuAuth) 기준으로 동적 관리
 */

import { isAdminRole } from './utils/role';
import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ToastProvider } from './components/ui';
import { AppShell } from './components/layout';
import {
  WorkflowsPage,
  WorkflowBuilderPage,
  KnowledgeBasesPage,
  TemplatesPage,
  AppsPage,
  MyAppsPage,
  AppDetailPage,
  AppSubmissionPage,
  AppShareRequestPage,
  MarketplacePage,
  ChatPage,
  LoginPage,
  InviteRegisterPage,
  AiProvidersPage,
  SystemSettingsPage,
  MenuManagementPage,
  AdminDashboardPage,
  UsersPage,
  TenantsPage,
  CustomersPage,
  PortalPage,
  UserHomePage,
  AuditLogsPage,
  FailureLogsPage,
  TokenCostPage,
  RolesPage,
  EmbeddingProvidersPage,
  ProviderConnectionPage,
  SpacesPage,
  SpaceDetailPage,
} from './pages';
import { PortalSidebar } from './components/layout';
import { authApi } from './api';
import {
  SiteLayout,
  HomePage as SiteHomePage,
  ProductPage as SiteProductPage,
  ContactPage as SiteContactPage,
} from './site';

// workflowId 바뀌면 ChatPage 재마운트 (세션/테마 초기화)
function ChatPageWithKey() {
  const { workflowId } = useParams<{ workflowId: string }>();
  return <ChatPage key={workflowId} />;
}

// 인증 필요한 라우트 래퍼 (로그인만 확인)
function PrivateRoute({ children }: { children: React.ReactNode }) {
  if (!authApi.isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

// 사용자 포탈 레이아웃 래퍼 (PortalSidebar + 페이지)
function PortalShell({ children }: { children: React.ReactNode }) {
  if (!authApi.isAuthenticated()) return <Navigate to="/login" replace />;
  return (
    <div className="portal-shell">
      <PortalSidebar
        activeView="workflows"
        onViewChange={() => {}}
        recentChats={[]}
        chatbots={[]}
        onSelectChatbot={() => {}}
        selectedChatbot={null}
      />
      <div className="portal-main" style={{ overflow: 'auto' }}>
        {children}
      </div>
    </div>
  );
}

// ── [기존 AdminRoute - DDB 메뉴 권한으로 대체됨] ─────────────────────────────
/*
function AdminRoute({ children }: { children: React.ReactNode }) {
  if (!authApi.isAuthenticated()) return <Navigate to="/login" replace />;
  const user = authApi.getUser();
  if (!isAdminRole(user?.role)) return <Navigate to="/portal" replace />;
  return <>{children}</>;
}
*/
// ─────────────────────────────────────────────────────────────────────────────


function App() {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    authApi.restoreAuth();

    // role에 따라 테마 적용 (<html data-role>, 로그인/로그아웃 시에도 authApi 에서 갱신)
    authApi.applyRoleTheme(authApi.getUser()?.role);

    setIsReady(true);
  }, []);

  if (!isReady) return null;

  return (
    <ToastProvider>
      <BrowserRouter>
        <Routes>
          {/* 로그인 (인증 불필요) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/invite" element={<InviteRegisterPage />} />

          {/* 공개 사이트 (인증 불필요) — 홈 / 제품소개 / 문의하기. 스타일은 .acx-site 로 격리 */}
          <Route element={<SiteLayout />}>
            <Route index element={<SiteHomePage />} />
            <Route path="product" element={<SiteProductPage />} />
            <Route path="contact" element={<SiteContactPage />} />
          </Route>

          {/* 모든 인증 사용자 — AppShell + Sidebar (path 없는 레이아웃 라우트: '/' 는 사이트 홈이 차지) */}
          <Route
            element={
              <PrivateRoute>
                <AppShell />
              </PrivateRoute>
            }
          >
            <Route path="workflows" element={<WorkflowsPage />} />
            <Route path="workflows/:id" element={<WorkflowBuilderPage />} />
            <Route path="marketplace" element={<MarketplacePage />} />
            <Route path="marketplace/:category" element={<MarketplacePage />} />
            <Route path="apps" element={<AppsPage />} />
            <Route path="apps/my" element={<MyAppsPage />} />
            <Route path="apps/:id" element={<AppDetailPage />} />
            <Route path="apps/my/:appId/submit" element={<AppSubmissionPage />} />
            <Route path="apps/submit" element={<AppShareRequestPage />} />
            {/* 라이브러리 (Apps → 라이브러리 리네이밍) */}
            <Route path="library" element={<AppsPage />} />
            <Route path="library/my" element={<MyAppsPage />} />
            <Route path="library/:id" element={<AppDetailPage />} />
            <Route path="library/my/:appId/submit" element={<AppSubmissionPage />} />
            <Route path="library/submit" element={<AppShareRequestPage />} />
            {/* 스페이스 */}
            <Route path="spaces" element={<SpacesPage />} />
            <Route path="spaces/:id" element={<SpaceDetailPage />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="templates/:category" element={<TemplatesPage />} />
            <Route path="knowledge-bases" element={<KnowledgeBasesPage />} />
            <Route path="knowledge-bases/db-connector" element={<KnowledgeBasesPage />} />
            <Route path="knowledge-bases/web-crawling" element={<KnowledgeBasesPage />} />
            <Route path="chat" element={<ChatPage />} />
            <Route path="chat/:workflowId" element={<ChatPageWithKey />} />
            {/* 관리자 메뉴 - /admin/* 평면 구조 */}
            <Route path="admin/providers" element={<AiProvidersPage />} />
            <Route path="admin/embeddingProviders" element={<EmbeddingProvidersPage />} />
            <Route path="admin/providerConnection" element={<ProviderConnectionPage embedded />} />
            <Route path="admin/system" element={<SystemSettingsPage />} />
            <Route path="admin/system/audit" element={<SystemSettingsPage />} />
            <Route path="admin/system/usage" element={<SystemSettingsPage />} />
            <Route path="admin/token-cost" element={<TokenCostPage />} />
            <Route path="admin/menus" element={<MenuManagementPage />} />
            <Route path="admin/users" element={<UsersPage />} />
            <Route path="admin/tenants" element={<TenantsPage />} />
            <Route path="admin/customers" element={<CustomersPage />} />
            <Route path="admin/dashboard" element={<AdminDashboardPage />} />
            {/* 로그 */}
            <Route path="admin/audit-logs" element={<AuditLogsPage />} />
            <Route path="admin/logs/audit" element={<AuditLogsPage />} />
            <Route path="admin/logs/failures" element={<FailureLogsPage />} />
            <Route path="admin/logs" element={<FailureLogsPage />} />
            <Route path="admin/roles" element={<RolesPage />} />
            <Route path="home" element={<UserHomePage />} />
          </Route>

          {/* 사용자 포탈 — 별도 레이아웃 (AppShell 밖) */}
          <Route path="/portal" element={<PrivateRoute><PortalPage /></PrivateRoute>} />
          <Route path="/portal/agents" element={<PrivateRoute><PortalPage /></PrivateRoute>} />
          <Route path="/portal/workflows" element={<PortalShell><WorkflowsPage /></PortalShell>} />
          <Route path="/portal/workflows/:id" element={<PortalShell><WorkflowBuilderPage /></PortalShell>} />
          <Route path="/portal/marketplace" element={<PortalShell><MarketplacePage /></PortalShell>} />
          <Route path="/portal/marketplace/:category" element={<PortalShell><MarketplacePage /></PortalShell>} />
          <Route path="/portal/templates" element={<PortalShell><TemplatesPage /></PortalShell>} />
          <Route path="/portal/templates/:category" element={<PortalShell><TemplatesPage /></PortalShell>} />
          <Route path="/portal/knowledge" element={<PortalShell><KnowledgeBasesPage /></PortalShell>} />
          <Route path="/portal/knowledge/web-crawling" element={<PortalShell><KnowledgeBasesPage /></PortalShell>} />
        </Routes>
      </BrowserRouter>
    </ToastProvider>
  );
}

export default App;
